import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import type {
  Invoice,
  InvoiceStatus,
  TenantDropdownItem,
  UpdateInvoiceStatusDto,
} from '../../types/billing';
import {
  ArrowLeft,
  RefreshCw,
  Loader2,
  FileText,
  AlertCircle,
  ChevronDown,
  Check,
  X,
  Clock,
  DollarSign,
  Calendar,
  Edit3,
  Eye,
  Filter,
  Search,
  Plus,
  Trash2,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

// Status options
const STATUS_OPTIONS: { value: InvoiceStatus; label: string; color: string }[] = [
  { value: 'draft' as InvoiceStatus, label: 'Rascunho', color: 'bg-bg-3 text-text-2' },
  { value: 'pending' as InvoiceStatus, label: 'Aguardando', color: 'bg-warning/10 text-warning' },
  { value: 'billed' as InvoiceStatus, label: 'Faturado', color: 'bg-info/10 text-accent-1' },
  { value: 'paid' as InvoiceStatus, label: 'Pago', color: 'bg-success/10 text-success' },
  { value: 'overdue' as InvoiceStatus, label: 'Vencido', color: 'bg-danger/10 text-danger' },
  { value: 'cancelled' as InvoiceStatus, label: 'Cancelado', color: 'bg-bg-3 text-text-3' },
];

const statusColors: Record<string, string> = {
  draft: 'bg-bg-3 text-text-2',
  pending: 'bg-warning/10 text-warning',
  billed: 'bg-info/10 text-accent-1',
  paid: 'bg-success/10 text-success',
  overdue: 'bg-danger/10 text-danger',
  cancelled: 'bg-bg-3 text-text-3',
};

const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  pending: 'Aguardando',
  billed: 'Faturado',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado',
};

/**
 * InvoiceManagement - Gerenciamento de faturas (Super Admin)
 */
export default function InvoiceManagement() {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tenants, setTenants] = useState<TenantDropdownItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Edit modal state
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editForm, setEditForm] = useState<{
    status: InvoiceStatus;
    boletoUrl: string;
    boletoNumber: string;
    notes: string;
  }>({
    status: 'draft' as InvoiceStatus,
    boletoUrl: '',
    boletoNumber: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // View modal state
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  // Generate invoice modal state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    tenantId: '',
    periodStart: '',
    periodEnd: '',
    status: 'draft' as InvoiceStatus,
  });
  const [generating, setGenerating] = useState(false);

  // Delete invoice modal state
  const [deletingInvoice, setDeletingInvoice] = useState<Invoice | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Redirect non-super-admin users
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/dashboard');
    }
  }, [isSuperAdmin, navigate]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
      fetchInvoices();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchInvoices();
    }
  }, [selectedTenantId, selectedStatus, isSuperAdmin]);

  const fetchTenants = async () => {
    try {
      if (!accessToken) return;
      const response = await apiClient.getBillingTenantsDropdown(accessToken);
      if (response.success && response.data) {
        setTenants(response.data);
      }
    } catch {
      // Failed to load tenants
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!accessToken) return;
      const params: any = { limit: 100 };
      if (selectedTenantId) params.tenantId = selectedTenantId;
      if (selectedStatus) params.status = selectedStatus;

      const response = await apiClient.getInvoices(params, accessToken);
      if (response.success && response.data) {
        setInvoices(response.data.invoices || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      status: invoice.status,
      boletoUrl: invoice.boletoUrl || '',
      boletoNumber: invoice.boletoNumber || '',
      notes: invoice.notes || '',
    });
  };

  const closeEditModal = () => {
    setEditingInvoice(null);
    setEditForm({
      status: 'draft' as InvoiceStatus,
      boletoUrl: '',
      boletoNumber: '',
      notes: '',
    });
  };

  const handleSave = async () => {
    if (!editingInvoice || !accessToken) return;

    setSaving(true);
    try {
      const updateData: UpdateInvoiceStatusDto = {
        status: editForm.status,
      };
      if (editForm.boletoUrl) updateData.boletoUrl = editForm.boletoUrl;
      if (editForm.boletoNumber) updateData.boletoNumber = editForm.boletoNumber;
      if (editForm.notes) updateData.notes = editForm.notes;

      const response = await apiClient.updateInvoiceStatus(
        editingInvoice.id,
        updateData,
        accessToken
      );

      if (response.success) {
        closeEditModal();
        fetchInvoices();
      } else {
        setError('Erro ao atualizar fatura');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const openViewModal = async (invoice: Invoice) => {
    try {
      if (!accessToken) return;
      const response = await apiClient.getInvoice(invoice.id, accessToken, true);
      if (response.success && response.data) {
        setViewingInvoice(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar detalhes');
    }
  };

  const openGenerateModal = () => {
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    setGenerateForm({
      tenantId: tenants.length > 0 ? tenants[0].id : '',
      periodStart: thirtyDaysAgo.toISOString().split('T')[0],
      periodEnd: today.toISOString().split('T')[0],
      status: 'draft' as InvoiceStatus,
    });
    setShowGenerateModal(true);
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setGenerateForm({
      tenantId: '',
      periodStart: '',
      periodEnd: '',
      status: 'draft' as InvoiceStatus,
    });
  };

  const handleGenerateInvoice = async () => {
    if (!generateForm.tenantId || !generateForm.periodStart || !generateForm.periodEnd || !accessToken) {
      setError('Preencha todos os campos');
      return;
    }

    // Validate dates
    const start = new Date(generateForm.periodStart);
    const end = new Date(generateForm.periodEnd);
    if (start >= end) {
      setError('A data de início deve ser anterior à data de fim');
      return;
    }

    setGenerating(true);
    try {
      const response = await apiClient.generateInvoice(
        generateForm.tenantId,
        {
          periodStart: generateForm.periodStart,
          periodEnd: generateForm.periodEnd,
          status: generateForm.status,
        },
        accessToken
      );

      if (response.success) {
        closeGenerateModal();
        fetchInvoices();
      } else {
        setError(response.message || 'Erro ao gerar fatura');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar fatura');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingInvoice || !accessToken) return;

    setDeleting(true);
    try {
      const response = await apiClient.deleteInvoice(deletingInvoice.id, accessToken);
      if (response.success) {
        setDeletingInvoice(null);
        fetchInvoices();
      } else {
        setError(response.message || 'Erro ao deletar fatura');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao deletar fatura');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatDate = (date: string | null): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatPeriod = (start: string, end: string): string => {
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T00:00:00');
    return `${startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - ${endDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header
        title="Gerenciamento de Faturas"
        subtitle="Visualize e gerencie faturas de todos os tenants"
      >
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 px-4 py-2 text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline">Voltar</span>
        </button>
        <button
          onClick={fetchInvoices}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-bg-3 hover:bg-bg-3/80 text-text-1 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
        <button
          onClick={openGenerateModal}
          className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Gerar Fatura</span>
        </button>
      </Header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6 bg-bg-2 p-4 rounded-lg border border-border-1">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={18} className="text-text-3" />
            <span className="text-sm font-medium text-text-2">Filtros</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tenant filter */}
            <div>
              <label className="block text-xs text-text-3 mb-1">Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="w-full appearance-none bg-bg-3 border border-border-1 rounded-lg px-4 py-2 pr-10 text-text-1 focus:outline-none focus:border-accent-1"
                >
                  <option value="">Todos os tenants</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-3 pointer-events-none"
                />
              </div>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-xs text-text-3 mb-1">Status</label>
              <div className="relative">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full appearance-none bg-bg-3 border border-border-1 rounded-lg px-4 py-2 pr-10 text-text-1 focus:outline-none focus:border-accent-1"
                >
                  <option value="">Todos os status</option>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={18}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-3 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-danger/10 border border-danger/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-danger" size={20} />
            <span className="text-danger">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-danger hover:text-danger/80">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <LoadingSpinner size="lg" />
        )}

        {/* Invoices Table */}
        {!loading && (
          <div className="bg-bg-2 rounded-lg border border-border-1 overflow-hidden">
            <div className="p-4 border-b border-border-1">
              <h3 className="text-lg font-semibold text-text-1 flex items-center gap-2">
                <FileText size={20} className="text-accent-1" />
                Faturas ({invoices.length})
              </h3>
            </div>

            {invoices.length === 0 ? (
              <div className="p-8 text-center text-text-3">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhuma fatura encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                        Período
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                        Disparos
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">
                        Faturado em
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">
                        Pago em
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-text-3 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-1">
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-bg-3/50 transition-colors">
                        <td className="px-4 py-4">
                          <span className="text-sm font-medium text-text-1">
                            {invoice.tenantName || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-text-1">
                            {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm text-text-1">
                            {formatNumber(invoice.totalDispatches)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-semibold text-success">
                            {formatCurrency(invoice.totalCost)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[invoice.status]}`}>
                            {statusLabels[invoice.status]}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm text-text-2">
                            {formatDate(invoice.billedAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm text-text-2">
                            {formatDate(invoice.paidAt)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openViewModal(invoice)}
                              className="p-2 text-text-3 hover:text-accent-1 hover:bg-bg-3 rounded-lg transition-colors"
                              title="Ver detalhes"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openEditModal(invoice)}
                              className="p-2 text-text-3 hover:text-accent-1 hover:bg-bg-3 rounded-lg transition-colors"
                              title="Editar status"
                            >
                              <Edit3 size={18} />
                            </button>
                            <button
                              onClick={() => setDeletingInvoice(invoice)}
                              disabled={invoice.status === 'paid'}
                              className={`p-2 rounded-lg transition-colors ${
                                invoice.status === 'paid'
                                  ? 'text-text-3/30 cursor-not-allowed'
                                  : 'text-text-3 hover:text-danger hover:bg-danger/10'
                              }`}
                              title={invoice.status === 'paid' ? 'Não é possível deletar faturas pagas' : 'Deletar fatura'}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {editingInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-lg bg-bg-1">
            <div className="p-6 border-b border-border-1">
              <h3 className="text-xl font-bold text-text-1">Atualizar Fatura</h3>
              <p className="text-sm text-text-3 mt-1">
                {editingInvoice.tenantName} - {formatPeriod(editingInvoice.periodStart, editingInvoice.periodEnd)}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {/* Current Info */}
              <div className="bg-bg-3 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-3">Total:</span>
                    <span className="ml-2 font-semibold text-text-1">{formatCurrency(editingInvoice.totalCost)}</span>
                  </div>
                  <div>
                    <span className="text-text-3">Disparos:</span>
                    <span className="ml-2 font-semibold text-text-1">{formatNumber(editingInvoice.totalDispatches)}</span>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">Status</label>
                <div className="relative">
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as InvoiceStatus })}
                    className="w-full appearance-none bg-bg-3 border border-border-1 rounded-lg px-4 py-3 pr-10 text-text-1 focus:outline-none focus:border-accent-1"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-3 pointer-events-none"
                  />
                </div>
              </div>

              {/* Boleto URL */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">URL do Boleto</label>
                <input
                  type="url"
                  value={editForm.boletoUrl}
                  onChange={(e) => setEditForm({ ...editForm, boletoUrl: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-bg-3 border border-border-1 rounded-lg px-4 py-2 text-text-1 focus:outline-none focus:border-accent-1"
                />
              </div>

              {/* Boleto Number */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">Número do Boleto</label>
                <input
                  type="text"
                  value={editForm.boletoNumber}
                  onChange={(e) => setEditForm({ ...editForm, boletoNumber: e.target.value })}
                  placeholder="Ex: 12345.67890..."
                  className="w-full bg-bg-3 border border-border-1 rounded-lg px-4 py-2 text-text-1 focus:outline-none focus:border-accent-1"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">Observações</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Notas sobre a fatura..."
                  rows={3}
                  className="w-full bg-bg-3 border border-border-1 rounded-lg px-4 py-2 text-text-1 focus:outline-none focus:border-accent-1 resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-border-1 flex gap-3 justify-end">
              <button
                onClick={closeEditModal}
                disabled={saving}
                className="px-4 py-2 text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-bg-1">
            <div className="p-6 border-b border-border-1 sticky top-0 bg-bg-1">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-text-1">Detalhes da Fatura</h3>
                  <p className="text-sm text-text-3 mt-1">
                    {viewingInvoice.tenantName} - {formatPeriod(viewingInvoice.periodStart, viewingInvoice.periodEnd)}
                  </p>
                </div>
                <button
                  onClick={() => setViewingInvoice(null)}
                  className="p-2 text-text-3 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary - Resumo com valor total e disparos cobrados */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-bg-3 p-4 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Valor Total</p>
                  <p className="text-lg font-bold text-success">
                    {formatCurrency(viewingInvoice.totalCost)}
                  </p>
                </div>
                <div className="bg-bg-3 p-4 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Cobrados</p>
                  <p className="text-lg font-bold text-text-1">
                    {formatNumber(viewingInvoice.utilityCount + viewingInvoice.marketingCount)}
                  </p>
                </div>
                <div className="bg-bg-3 p-4 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Gratuitos</p>
                  <p className="text-lg font-bold text-text-1">
                    {formatNumber(viewingInvoice.freeDispatches)}
                  </p>
                </div>
                <div className="bg-bg-3 p-4 rounded-lg">
                  <p className="text-xs text-text-3 uppercase">Status</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[viewingInvoice.status]}`}>
                    {statusLabels[viewingInvoice.status]}
                  </span>
                </div>
              </div>

              {/* Breakdown por Categoria - com quantidade cobrada e custo */}
              <div>
                <h4 className="text-sm font-semibold text-text-2 mb-3">Breakdown por Categoria</h4>
                <div className="bg-bg-3 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-bg-1">
                      <tr>
                        <th className="px-3 py-2 text-left text-text-3">Categoria</th>
                        <th className="px-3 py-2 text-right text-text-3">Qtd Cobrada</th>
                        <th className="px-3 py-2 text-right text-text-3">Custo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-1">
                      <tr>
                        <td className="px-3 py-2 text-text-1">UTILITY</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.utilityCount)}</td>
                        <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(viewingInvoice.utilityCost)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 text-text-1">MARKETING</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.marketingCount)}</td>
                        <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(viewingInvoice.marketingCost)}</td>
                      </tr>
                      <tr className="bg-bg-1 font-semibold">
                        <td className="px-3 py-2 text-text-1">TOTAL</td>
                        <td className="px-3 py-2 text-right text-text-1">{formatNumber(viewingInvoice.utilityCount + viewingInvoice.marketingCount)}</td>
                        <td className="px-3 py-2 text-right text-success">{formatCurrency(viewingInvoice.totalCost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Faturado em</h4>
                  <p className="text-text-1">{formatDate(viewingInvoice.billedAt)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Pago em</h4>
                  <p className="text-text-1">{formatDate(viewingInvoice.paidAt)}</p>
                </div>
              </div>

              {/* Boleto info */}
              {(viewingInvoice.boletoUrl || viewingInvoice.boletoNumber) && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Boleto</h4>
                  <div className="bg-bg-3 p-4 rounded-lg space-y-2">
                    {viewingInvoice.boletoNumber && (
                      <p className="text-sm text-text-1 font-mono">{viewingInvoice.boletoNumber}</p>
                    )}
                    {viewingInvoice.boletoUrl && (
                      <a
                        href={viewingInvoice.boletoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-accent-1 hover:underline"
                      >
                        Abrir boleto
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {viewingInvoice.notes && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-2">Observações</h4>
                  <p className="text-sm text-text-1 bg-bg-3 p-4 rounded-lg">{viewingInvoice.notes}</p>
                </div>
              )}

              {/* WABA Breakdown - com quantidade por categoria */}
              {viewingInvoice.breakdown?.byWaba && viewingInvoice.breakdown.byWaba.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-text-2 mb-3">Por Conta WABA</h4>
                  <div className="bg-bg-3 rounded-lg overflow-hidden overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-bg-1">
                        <tr>
                          <th className="px-3 py-2 text-left text-text-3">Número</th>
                          <th className="px-3 py-2 text-right text-text-3">UTILITY</th>
                          <th className="px-3 py-2 text-right text-text-3">MARKETING</th>
                          <th className="px-3 py-2 text-right text-text-3">Total</th>
                          <th className="px-3 py-2 text-right text-text-3">Custo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-1">
                        {viewingInvoice.breakdown.byWaba.map((w, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-text-1">{w.displayPhoneNumber}</td>
                            <td className="px-3 py-2 text-right text-text-1">{formatNumber(w.utilityCount || 0)}</td>
                            <td className="px-3 py-2 text-right text-text-1">{formatNumber(w.marketingCount || 0)}</td>
                            <td className="px-3 py-2 text-right text-text-1 font-medium">
                              {formatNumber((w.utilityCount || 0) + (w.marketingCount || 0))}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-success">{formatCurrency(w.totalCost)}</td>
                          </tr>
                        ))}
                        {/* Linha de totais */}
                        <tr className="bg-bg-1 font-semibold">
                          <td className="px-3 py-2 text-text-1">TOTAL</td>
                          <td className="px-3 py-2 text-right text-text-1">
                            {formatNumber(viewingInvoice.breakdown.byWaba.reduce((sum, w) => sum + (w.utilityCount || 0), 0))}
                          </td>
                          <td className="px-3 py-2 text-right text-text-1">
                            {formatNumber(viewingInvoice.breakdown.byWaba.reduce((sum, w) => sum + (w.marketingCount || 0), 0))}
                          </td>
                          <td className="px-3 py-2 text-right text-text-1">
                            {formatNumber(viewingInvoice.breakdown.byWaba.reduce((sum, w) => sum + (w.utilityCount || 0) + (w.marketingCount || 0), 0))}
                          </td>
                          <td className="px-3 py-2 text-right text-success">
                            {formatCurrency(viewingInvoice.breakdown.byWaba.reduce((sum, w) => sum + w.totalCost, 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Invoice Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-lg bg-bg-1">
            <div className="p-6 border-b border-border-1">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-1/10 rounded-lg">
                  <Plus size={20} className="text-accent-1" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-1">Gerar Nova Fatura</h3>
                  <p className="text-sm text-text-3">Selecione o tenant e o período de cobrança</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Tenant Selection */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">
                  Tenant / Empresa
                </label>
                <div className="relative">
                  <select
                    value={generateForm.tenantId}
                    onChange={(e) => setGenerateForm({ ...generateForm, tenantId: e.target.value })}
                    className="w-full appearance-none bg-bg-3 border border-border-1 rounded-lg px-4 py-3 pr-10 text-text-1 focus:outline-none focus:border-accent-1"
                  >
                    <option value="">Selecione um tenant</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-3 pointer-events-none"
                  />
                </div>
              </div>

              {/* Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-2">
                    <Calendar size={14} className="inline mr-1" />
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={generateForm.periodStart}
                    onChange={(e) => setGenerateForm({ ...generateForm, periodStart: e.target.value })}
                    className="w-full bg-bg-3 border border-border-1 rounded-lg px-4 py-3 text-text-1 focus:outline-none focus:border-accent-1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-2">
                    <Calendar size={14} className="inline mr-1" />
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={generateForm.periodEnd}
                    onChange={(e) => setGenerateForm({ ...generateForm, periodEnd: e.target.value })}
                    className="w-full bg-bg-3 border border-border-1 rounded-lg px-4 py-3 text-text-1 focus:outline-none focus:border-accent-1"
                  />
                </div>
              </div>

              {/* Initial Status */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">Status Inicial</label>
                <div className="grid grid-cols-3 gap-2">
                  {STATUS_OPTIONS.slice(0, 3).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setGenerateForm({ ...generateForm, status: opt.value })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        generateForm.status === opt.value
                          ? opt.color + ' ring-2 ring-accent-1'
                          : 'bg-bg-3 text-text-2 hover:bg-bg-3/80'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Info */}
              <div className="bg-info/10 p-4 rounded-lg border border-info/30">
                <p className="text-sm text-accent-1">
                  A fatura será gerada com os custos de todos os disparos realizados no período selecionado.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-border-1 flex gap-3 justify-end">
              <button
                onClick={closeGenerateModal}
                disabled={generating}
                className="px-4 py-2 text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateInvoice}
                disabled={generating || !generateForm.tenantId || !generateForm.periodStart || !generateForm.periodEnd}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {generating ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                Gerar Fatura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="rounded-xl shadow-2xl w-full max-w-md bg-bg-1">
            <div className="p-6 border-b border-border-1">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-danger/10 rounded-lg">
                  <Trash2 size={20} className="text-danger" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-1">Deletar Fatura</h3>
                  <p className="text-sm text-text-3">Esta ação não pode ser desfeita</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-text-1 mb-4">
                Tem certeza que deseja deletar esta fatura?
              </p>

              <div className="bg-bg-3 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-3">Tenant:</span>
                  <span className="text-text-1 font-medium">{deletingInvoice.tenantName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-3">Período:</span>
                  <span className="text-text-1">{formatPeriod(deletingInvoice.periodStart, deletingInvoice.periodEnd)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-3">Valor:</span>
                  <span className="text-success font-medium">{formatCurrency(deletingInvoice.totalCost)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-3">Status:</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[deletingInvoice.status]}`}>
                    {statusLabels[deletingInvoice.status]}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border-1 flex gap-3 justify-end">
              <button
                onClick={() => setDeletingInvoice(null)}
                disabled={deleting}
                className="px-4 py-2 text-text-2 hover:text-text-1 hover:bg-bg-3 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-danger hover:bg-danger/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
